// frontend/src/hooks/useSidebar.js
// Enterprise-grade sidebar state management with keyboard shortcuts and persistence

import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for managing sidebar state with keyboard shortcuts and persistence
 * Follows React hooks best practices with proper cleanup and performance optimization
 *
 * @param {Object} options - Hook configuration options
 * @returns {Object} Sidebar state and controls
 */
export const useSidebar = (options = {}) => {
  const {
    defaultCollapsed = false,
    persistKey = 'notes.sidebar.collapsed',
    keyboardShortcut = { key: 'b', metaKey: true, ctrlKey: true },
    enableKeyboardShortcut = true,
    onStateChange = null,
    animationDuration = 300
  } = options;

  // State management
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(persistKey);
      return stored !== null ? JSON.parse(stored) : defaultCollapsed;
    } catch (error) {
      console.warn('useSidebar: Failed to load from localStorage:', error);
      return defaultCollapsed;
    }
  });

  const [isAnimating, setIsAnimating] = useState(false);

  // Refs for cleanup and performance
  const animationTimeoutRef = useRef(null);
  const keyboardListenerRef = useRef(null);

  /**
   * Toggle sidebar state with animation handling
   */
  const toggle = useCallback((options = {}) => {
    const { persist = true } = options;

    setIsAnimating(true);

    const newState = !isCollapsed;
    setIsCollapsed(newState);

    // Handle animation timing
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, animationDuration);

    // Only persist if requested
    if (persist) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(newState));
      } catch (error) {
        console.warn('useSidebar: Failed to save to localStorage:', error);
      }
    }

    // Notify parent component if callback provided
    if (onStateChange && typeof onStateChange === 'function') {
      onStateChange(newState);
    }
  }, [isCollapsed, persistKey, onStateChange, animationDuration]);

  /**
   * Collapse sidebar programmatically
   */
  const collapse = useCallback(() => {
    if (!isCollapsed) {
      toggle();
    }
  }, [isCollapsed, toggle]);

  /**
   * Collapse sidebar temporarily without persisting to localStorage
   */
  const collapseTemporary = useCallback(() => {
    if (!isCollapsed) {
      toggle({ persist: false });
    }
  }, [isCollapsed, toggle]);

  /**
   * Expand sidebar programmatically
   */
  const expand = useCallback(() => {
    if (isCollapsed) {
      toggle();
    }
  }, [isCollapsed, toggle]);

  /**
   * Set sidebar state directly
   */
  const setState = useCallback((collapsed) => {
    if (collapsed !== isCollapsed) {
      toggle();
    }
  }, [isCollapsed, toggle]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyboard = useCallback((event) => {
    if (!enableKeyboardShortcut) return;

    const { key, metaKey, ctrlKey, altKey, shiftKey } = keyboardShortcut;

    // Check if the keyboard event matches our shortcut
    const matchesKey = event.key.toLowerCase() === key.toLowerCase();
    const matchesMeta = metaKey ? event.metaKey || event.ctrlKey : !event.metaKey && !event.ctrlKey;
    const matchesCtrl = ctrlKey ? event.ctrlKey : !event.ctrlKey;
    const matchesAlt = altKey ? event.altKey : !event.altKey;
    const matchesShift = shiftKey ? event.shiftKey : !event.shiftKey;

    if (matchesKey && matchesMeta && matchesCtrl && matchesAlt && matchesShift) {
      event.preventDefault();
      event.stopPropagation();
      toggle();
    }
  }, [keyboardShortcut, enableKeyboardShortcut, toggle]);

  /**
   * Setup keyboard event listener
   */
  useEffect(() => {
    if (!enableKeyboardShortcut) return;

    // Store reference for cleanup
    keyboardListenerRef.current = handleKeyboard;

    // Add event listener
    document.addEventListener('keydown', handleKeyboard, { passive: false });

    // Cleanup function
    return () => {
      if (keyboardListenerRef.current) {
        document.removeEventListener('keydown', keyboardListenerRef.current);
        keyboardListenerRef.current = null;
      }
    };
  }, [handleKeyboard, enableKeyboardShortcut]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Get CSS properties for sidebar width animation
   */
  const getSidebarStyles = useCallback((collapsedWidth = 0, expandedWidth = 320) => {
    const width = isCollapsed ? collapsedWidth : expandedWidth;

    return {
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
      transition: `all ${animationDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
      overflow: 'hidden',
      opacity: isCollapsed ? 0 : 1
    };
  }, [isCollapsed, animationDuration]);

  /**
   * Get CSS properties for main content area
   */
  const getContentStyles = useCallback((sidebarWidth = 320, collapsedWidth = 0) => {
    const marginLeft = isCollapsed ? collapsedWidth : sidebarWidth;

    return {
      marginLeft: `${marginLeft}px`,
      transition: `margin-left ${animationDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
      width: `calc(100% - ${marginLeft}px)`
    };
  }, [isCollapsed, animationDuration]);

  /**
   * Get keyboard shortcut display string
   */
  const getShortcutDisplay = useCallback(() => {
    if (!enableKeyboardShortcut) return '';

    const { key, metaKey, ctrlKey, altKey, shiftKey } = keyboardShortcut;
    const parts = [];

    // Handle modifier keys individually to support combinations like Cmd+Ctrl
    if (metaKey) {
      parts.push(navigator.platform.includes('Mac') ? '⌘' : 'Ctrl');
    }
    if (ctrlKey && metaKey) {
      // If both metaKey and ctrlKey are required, add the control symbol
      parts.push(navigator.platform.includes('Mac') ? '⌃' : 'Ctrl');
    } else if (ctrlKey && !metaKey) {
      // If only ctrlKey is required
      parts.push(navigator.platform.includes('Mac') ? '⌃' : 'Ctrl');
    }
    if (altKey) {
      parts.push(navigator.platform.includes('Mac') ? '⌥' : 'Alt');
    }
    if (shiftKey) {
      parts.push('⇧');
    }
    parts.push(key.toUpperCase());

    return parts.join('');
  }, [keyboardShortcut, enableKeyboardShortcut]);

  // Return hook interface
  return {
    // State
    isCollapsed,
    isAnimating,

    // Actions
    toggle,
    collapse,
    collapseTemporary,
    expand,
    setState,

    // Utilities
    getSidebarStyles,
    getContentStyles,
    getShortcutDisplay,

    // Configuration
    shortcut: getShortcutDisplay(),
    animationDuration
  };
};